import { AfterContentInit, Component, Inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../_services/auth.service';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { Location as LocationModel } from '../_models/user';
import { Subject } from 'rxjs';
import { Location } from '@angular/common';
import {
  MsalBroadcastService,
  MsalGuardConfiguration,
  MsalService,
  MSAL_GUARD_CONFIG,
} from '@azure/msal-angular';
import {
  AuthenticationResult,
  BrowserUtils,
  EventMessage,
  EventType,
  InteractionStatus,
  PopupRequest,
} from '@azure/msal-browser';
import { AppService } from '../_services/app.service';
import { filter, takeUntil } from 'rxjs/operators';
import { JwksValidationHandler } from 'angular-oauth2-oidc-jwks';
import { OAuthService } from 'angular-oauth2-oidc';
import { authConfig, teamConfig } from '../_services/sso.config';
import { NgxSpinnerService } from 'ngx-spinner';
import { ErrorDiagnosticService } from '../_services/error-diagnostic.service';

@Component({
  standalone: false,
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit, AfterContentInit {
  loading: boolean = false;
  locationRoles: LocationModel[];
  selectedLocation: LocationModel;

  isIframe = false;
  loginDisplay = false;
  private readonly _destroying$ = new Subject<void>();

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private msalAuthService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private diagnosticService: ErrorDiagnosticService,
    private location: Location,
    private activatedRoute: ActivatedRoute,
    private oauthService: OAuthService,
    private appService: AppService,
    private router: Router,
    private authService: AuthService,
    private toastr: ToastrService,
    private translateService: TranslateService,
    private spinner: NgxSpinnerService,
  ) {}

  ngAfterContentInit() {
    this.diagnosticService.setTrace('LoginComponent.ngAfterContentInit');

    this.activatedRoute.queryParams.subscribe((params) => {
      if (this.appService.inPortal()) {
        if (params['code']) {
          // if (!!this.authService.currentUserValue) {
          //   this.setLocations(this.authService.currentUserValue);
          // }
          // else {
          this.authService.teamAuth(params).subscribe(
            (data) => {
              this.setLocations(this.authService.currentUserValue);
            },
            (err) => {
              this.errLogout('team', err);
            },
          );
          // }
        } else {
          this.authService.clearStores();
          this.configureSingleSignOn();
          this.submit();
        }
      }
    });
  }

  ngOnInit() {
    this.diagnosticService.setMethodName('Login-APP');

    const currentPath = this.location.path();
    this.isIframe =
      BrowserUtils.isInIframe() &&
      !window.opener &&
      currentPath.indexOf('logout') < 0;
    this.setLoginDisplay();
    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None,
        ),
        takeUntil(this._destroying$),
      )
      .subscribe(() => {
        this.setLoginDisplay();
        this.checkAndSetActiveAccount();
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS,
        ),
      )
      .subscribe((result: EventMessage) => {
        const payload = result.payload as AuthenticationResult;
        this.msalAuthService.instance.setActiveAccount(payload.account);
        this.spinner.show();
        this.authService.adAuth(payload).subscribe(
          (locations) => {
            if (locations) {
              this.setLocations(this.authService.currentUserValue);
            } else {
              this.errLogout('ad', null);
            }
            this.spinner.hide();
          },
          (err) => {
            this.errLogout('ad', err);
            this.spinner.hide();
          },
        );
      });

    this.msalAuthService.handleRedirectObservable().subscribe({
      next: (result: AuthenticationResult) => {
        if (!this.appService.inPortal()) {
          if (this.msalAuthService.instance.getAllAccounts().length === 0)
            this.submit();
          if (this.loginDisplay) {
            if (this.authService.currentUserValue.locationRoles?.length)
              this.setLocations(this.authService.currentUserValue);
            //this.router.navigateByUrl('/home');
          }
        }
      },
      error: (error) => {
        console.log(error);
      },
    });
  }

  errLogout(prov: string, err) {
    this.diagnosticService.setTrace('errLogout');

    if (prov === 'team') {
      if (!!err && err.toLowerCase().includes('signer')) {
        this.toastr.error(err, this.translateService.instant('error.title'));
      } else {
        // this.toastr.error(this.translateService.instant('login.error'), this.translateService.instant('error.title'));
        this.configureSingleSignOn();
        this.submit();
      }
      throw Error(err);
    } else {
      this.toastr.error('Error while auth.');
      setTimeout(() => {
        // this.authService.logout();
      }, 3000);
    }
  }

  setLocations(data: any) {
    if (!!!data.locationRoles?.length) {
      this.authService.logout();
      //this.authService.logoutProv();
      return;
    }

    if (data?.locationRoles?.length > 1) {
      this.locationRoles = JSON.parse(JSON.stringify(data.locationRoles));
      this.selectedLocation = this.locationRoles[0];
      this.loginDisplay = true;
    } else {
      this.profile(
        data.locationRoles[0].LocationId,
        data.locationRoles[0].RoleId,
        data.locationRoles[0].Id,
      );
    }
  }

  profile(locationId, roleId, id) {
    this.loading = true;
    this.authService.getProfile(locationId, roleId).subscribe(
      (data) => {
        this.loading = false;
        if (data) {
          this.authService.changeCurrentLocation(id);
          this.router.navigateByUrl('/home');
        }
        if (!!!data.columns.length)
          this.toastr.error(this.translateService.instant('error.empty'));
      },
      (err) => {
        this.loading = false;
        this.toastr.error(err, this.translateService.instant('error.title'));
        setTimeout(() => {
          this.authService.logoutProv();
        }, 3000);
      },
    );
  }

  onSelectedChange(value: LocationModel) {
    this.selectedLocation = value;
  }

  setLoginDisplay() {
    this.loginDisplay =
      this.msalAuthService.instance.getAllAccounts().length > 0;
  }

  checkAndSetActiveAccount() {
    let activeAccount = this.msalAuthService.instance.getActiveAccount();
    if (
      !activeAccount &&
      this.msalAuthService.instance.getAllAccounts().length > 0
    ) {
      let accounts = this.msalAuthService.instance.getAllAccounts();
      this.msalAuthService.instance.setActiveAccount(accounts[0]);
    }
  }

  // async loginPopup() {
  //   if (this.msalGuardConfig.authRequest) {
  //     await this.msalAuthService.loginPopup({ ...this.msalGuardConfig.authRequest } as PopupRequest)
  //       .subscribe((response: AuthenticationResult) => {
  //         this.msalAuthService.instance.setActiveAccount(response.account);
  //       });
  //   } else {
  //     await this.msalAuthService.loginPopup()
  //       .subscribe((response: AuthenticationResult) => {
  //         this.msalAuthService.instance.setActiveAccount(response.account);
  //       });
  //   }
  // }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  submit() {
    if (this.appService.inPortal()) {
      this.diagnosticService.setTrace('submit.inPortal');
      this.oauthService.initImplicitFlow();
    } else {
      this.diagnosticService.setTrace('submit not is inPortal');
      this.authService.loginRedirectAd();
    }
  }

  configureSingleSignOn() {
    const code_verifier = this.appService.makeString(128);
    this.authService.generateCodeChallenge(code_verifier).then((challenge) => {
      this.oauthService.configure(authConfig(challenge));
      this.oauthService.tokenValidationHandler = new JwksValidationHandler();
      this.oauthService.loadDiscoveryDocument(teamConfig.discoveryUrl);
      sessionStorage.setItem('c_ver', code_verifier);
    });
  }
}
